import React, { useState, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, LibraryIcon, LampDeskIcon, CheckIcon, SparklesIcon, CameraIcon, AlertTriangleIcon } from 'lucide-react';
import { UpsellPopup } from './UpsellPopup';
import { trackAddToCart } from '../utils/metaPixel';
function OrderBumpCard({
  checked,
  onChange,
  title,
  children,
  image,
  icon
}: {
  checked: boolean;
  onChange: () => void;
  title: string;
  children: React.ReactNode;
  image?: string;
  icon?: React.ReactNode;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 15
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} className={`relative rounded-2xl border-2 transition-all duration-300 overflow-hidden text-left cursor-pointer ${checked ? 'border-accent bg-accent/5 shadow-[0_0_20px_rgba(212,168,83,0.15)]' : 'border-white/10 bg-surface hover:border-white/20'}`} onClick={onChange} data-id="element-170">
      {/* Dashed top accent */}
      <div className="h-1 w-full bg-[repeating-linear-gradient(90deg,#D4A853_0px,#D4A853_8px,transparent_8px,transparent_16px)]" data-id="element-171"></div>

      <div className="p-6 sm:p-8" data-id="element-172">
        <div className="flex gap-5" data-id="element-173">
          {/* Checkbox */}
          <div className="flex-shrink-0 pt-1" data-id="element-174">
            <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${checked ? 'bg-accent border-accent' : 'border-textMuted/40 bg-transparent'}`} data-id="element-175">
              {checked && <CheckIcon className="w-4 h-4 text-background" data-id="element-176" />}
            </div>
          </div>

          {/* Content */}
          <div className="flex-grow space-y-4" data-id="element-177">
            <h4 className="font-serif text-lg sm:text-xl text-text leading-snug" data-id="element-178">
              {title}
            </h4>

            {/* Image or Icon */}
            {image && <div className="rounded-xl overflow-hidden border border-white/10" data-id="element-179">
                <img src={image} alt={title} loading="lazy" decoding="async" className="w-full h-48 sm:h-56 object-cover" data-id="element-180" />
              </div>}
            {!image && icon && <div className="w-14 h-14 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center" data-id="element-181">
                {icon}
              </div>}

            <div className="text-sm sm:text-base text-textMuted leading-relaxed space-y-3" data-id="element-182">
              {children}
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function PricingSection() {
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const totalPrice = 9.99 + (bump1 ? 9.99 : 0) + (bump2 ? 9.99 : 0);
  const getCheckoutUrl = () => {
    if (bump1 && bump2) return 'https://rutinasimple.com//cart/52247755325721:1';
    if (bump1) return 'https://rutinasimple.com//cart/52247750476057:1';
    if (bump2) return 'https://rutinasimple.com//cart/52247754834201:1';
    return 'https://rutinasimple.com//cart/52247734026521:1';
  };
  const handleCheckout = () => {
    if (!bump1 && !bump2) {
      setShowUpsell(true);
    } else {
      trackAddToCart(totalPrice);
      window.open(getCheckoutUrl(), '_blank');
    }
  };
  return <section id="pricing" className="py-12 sm:py-24 px-4 bg-background relative overflow-hidden" data-id="element-183">
      {/* Cinematic lighting effect */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[400px] bg-accent/10 blur-[120px] pointer-events-none" data-id="element-184"></div>

      <div className="max-w-3xl mx-auto text-center relative z-10" data-id="element-185">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="space-y-8" data-id="element-186">
          <p className="text-lg text-textMuted leading-relaxed" data-id="element-187">
            Normalmente, el acceso a este bundle de diseño cuesta $97 USD. Pero
            como estamos validando nuestro nuevo portal para la comunidad
            creativa de LATAM y España...
          </p>

          <div className="py-8" data-id="element-188">
            {/* Offer summary */}
            <div className="mb-8 max-w-lg mx-auto bg-surface rounded-2xl border border-white/10 p-6 text-left space-y-3" data-id="element-189">
              <h3 className="font-serif text-xl text-text text-center mb-4" data-id="element-190">
                Tu Bundle Incluye:
              </h3>
              <ul className="space-y-2 text-sm text-textMuted" data-id="element-191">
                <li className="flex items-center gap-2" data-id="element-192">
                  <CheckIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-193" />
                  <span data-id="element-194">
                    Ebook Maestro "Sculpt-It"{' '}
                    <span className="text-accent font-medium" data-id="element-195">(Valor $27)</span>
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-196">
                  <CheckIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-197" />
                  <span data-id="element-198">
                    Psicología del Color DIY{' '}
                    <span className="text-accent font-medium" data-id="element-199">(Valor $17)</span>
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-200">
                  <CheckIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-201" />
                  <span data-id="element-202">
                    Plantilla Gigante T-Rex{' '}
                    <span className="text-accent font-medium" data-id="element-203">(Valor $15)</span>
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-204">
                  <CheckIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-205" />
                  <span data-id="element-206">
                    Checklist de Ahorro 70%{' '}
                    <span className="text-accent font-medium" data-id="element-207">(Valor $7)</span>
                  </span>
                </li>
                <li className="flex items-center gap-2" data-id="element-208">
                  <CheckIcon className="w-4 h-4 text-accent flex-shrink-0" data-id="element-209" />
                  <span data-id="element-210">
                    El Negocio del Papercraft{' '}
                    <span className="text-accent font-medium" data-id="element-211">(Valor $27)</span>
                  </span>
                </li>
              </ul>
              <div className="pt-3 border-t border-white/10 text-center" data-id="element-212">
                <span className="text-textMuted text-sm" data-id="element-213">Valor Total: </span>
                <span className="text-text font-serif text-lg line-through decoration-danger" data-id="element-214">
                  $93 USD
                </span>
              </div>
            </div>

            <div className="flex flex-col items-center justify-center gap-2 mb-6" data-id="element-215">
              <span className="text-2xl text-textMuted line-through decoration-danger" data-id="element-216">
                No pagarás $97
              </span>
              <span className="text-xl text-textMuted line-through decoration-danger" data-id="element-217">
                Ni siquiera $47
              </span>
            </div>

            <p className="text-sm uppercase tracking-widest text-accent font-bold mb-4" data-id="element-218">
              HOY ACCESO TOTAL POR SOLO:
            </p>
            <button onClick={handleCheckout} className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-accent text-background px-12 py-6 rounded-xl font-bold text-xl sm:text-2xl transition-all hover:bg-accentHover hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_50px_rgba(212,168,83,0.4)]" data-id="element-219">
              <span data-id="element-220">Obtener Todo por ${totalPrice.toFixed(2)} USD</span>
              <ArrowRightIcon className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-221" />
            </button>
          </div>

          {/* ORDER BUMPS */}
          <div className="space-y-5 text-left max-w-2xl mx-auto" data-id="element-222">
            {/* Order Bump #1 */}
            <OrderBumpCard checked={bump1} onChange={() => setBump1(!bump1)} title="SÍ! Desbloquea mi Librería de 50 Diseños de Autor (Ahorra $140 USD)" image="https://cdn.magicpatterns.com/uploads/r2KUtWvZggGoXSiNMeJwWC/order_bump-1.png" data-id="element-223">
              <p data-id="element-224">
                No te detengas con un solo proyecto. Por solo{' '}
                <strong className="text-text" data-id="element-225">$9.99</strong> (menos de lo que
                cuesta una hamburguesa), recibe acceso instantáneo a mi
                colección privada de 50 plantillas premium.
              </p>
              <p data-id="element-226">
                <strong className="text-text" data-id="element-227">Incluye:</strong> Animales de
                safari, máscaras geométricas, trofeos de pared y piezas de
                decoración moderna. Si compraras cada una por separado en
                nuestra tienda, pagarías $150 USD. Hoy, te las llevas por{' '}
                <span className="text-accent font-semibold" data-id="element-228">
                  $0.20 centavos cada una.
                </span>
              </p>
              <p className="italic text-textMuted/80" data-id="element-229">
                ¿Por qué conformarte con una escultura cuando puedes tener una
                galería completa en casa?
              </p>
              <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all ${bump1 ? 'bg-accent text-background' : 'bg-surfaceLight text-accent border border-accent/30'}`} data-id="element-230">
                <LibraryIcon className="w-4 h-4" data-id="element-231" />
                <span data-id="element-232">SÍ, AGREGAR LAS 50 PLANTILLAS POR $9.99</span>
              </div>
            </OrderBumpCard>

            {/* Order Bump #2 */}
            <OrderBumpCard checked={bump2} onChange={() => setBump2(!bump2)} title='SÍ! Haz que tus obras brillen con la Guía "Studio Glow"' image="https://cdn.magicpatterns.com/uploads/wihCKhKsGoKPHZebrPbBBG/order_bumpt-2.png" data-id="element-233">
              <p data-id="element-234">
                Seamos honestos: nadie quiere que su arte parezca un "proyecto
                escolar" de cartulina barata. La diferencia entre una manualidad
                y una pieza de galería es la iluminación y la textura.
              </p>
              <p className="font-medium text-text" data-id="element-235">
                Con esta guía digital rápida, aprenderás:
              </p>
              <ul className="space-y-2" data-id="element-236">
                <li className="flex items-start gap-2" data-id="element-237">
                  <LampDeskIcon className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" data-id="element-238" />
                  <span data-id="element-239">
                    Cómo usar lámparas de escritorio comunes para crear sombras
                    dramáticas.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-240">
                  <SparklesIcon className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" data-id="element-241" />
                  <span data-id="element-242">
                    Mi lista secreta de papeles que parecen metal y concreto.
                  </span>
                </li>
                <li className="flex items-start gap-2" data-id="element-243">
                  <CameraIcon className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" data-id="element-244" />
                  <span data-id="element-245">
                    El truco de 30 segundos en tu celular para que tus fotos se
                    vuelvan virales en Instagram.
                  </span>
                </li>
              </ul>
              <p className="italic text-textMuted/80" data-id="element-246">
                Lleva tu acabado al nivel profesional por solo $9.99
                adicionales. Tu arte merece verse espectacular.
              </p>
              <div className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition-all ${bump2 ? 'bg-accent text-background' : 'bg-surfaceLight text-accent border border-accent/30'}`} data-id="element-247">
                <SparklesIcon className="w-4 h-4" data-id="element-248" />
                <span data-id="element-249">SÍ, QUIERO EL ACABADO PROFESIONAL POR $9.99</span>
              </div>
            </OrderBumpCard>
          </div>

          <button onClick={handleCheckout} className="group relative w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-accent text-background px-12 py-6 rounded-xl font-bold text-xl sm:text-2xl transition-all hover:bg-accentHover hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_50px_rgba(212,168,83,0.4)]" data-id="element-250">
            <span data-id="element-251">Sí, Quiero Todo por ${totalPrice.toFixed(2)} USD</span>
            <ArrowRightIcon className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-252" />
          </button>

          <div className="flex items-center justify-center gap-2 text-sm text-textMuted mt-6" data-id="element-253">
            <ShieldCheckIcon className="w-4 h-4 text-accent" data-id="element-254" />
            <span data-id="element-255">Pago 100% Seguro y Encriptado</span>
          </div>

          <div className="mt-8 max-w-xl mx-auto space-y-4" data-id="element-256">
            {/* Progress bar */}
            <div className="bg-surface rounded-2xl border border-white/10 p-5" data-id="element-257">
              <div className="flex items-center justify-between mb-3" data-id="element-258">
                <span className="text-sm font-medium text-text" data-id="element-259">
                  Lugares de Fundador Ocupados
                </span>
                <span className="text-sm font-bold text-accent" data-id="element-260">425 / 500</span>
              </div>
              <div className="w-full h-3 bg-background rounded-full overflow-hidden border border-white/5" data-id="element-261">
                <div className="h-full bg-gradient-to-r from-accent to-accentHover rounded-full transition-all duration-1000 relative" style={{
                width: '85%'
              }} data-id="element-262">
                  <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full" data-id="element-263"></div>
                </div>
              </div>
              <p className="text-xs text-textMuted mt-3 text-center" data-id="element-264">
                Solo quedan <strong className="text-danger" data-id="element-265">75 lugares</strong>{' '}
                al precio de lanzamiento
              </p>
            </div>

            <div className="p-4 border border-danger/30 bg-danger/5 rounded-lg" data-id="element-266">
              <p className="text-sm text-textMuted flex items-start gap-2" data-id="element-267">
                <AlertTriangleIcon className="w-4 h-4 text-danger mt-0.5 flex-shrink-0" data-id="element-268" />
                <span data-id="element-269">
                  <strong className="text-danger" data-id="element-270">ATENCIÓN:</strong> Esta oferta
                  es válida solo mientras esta página esté activa. Una vez que
                  alcancemos los 500 fundadores, el precio regresará a su valor
                  original.
                </span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      <UpsellPopup isOpen={showUpsell} onAccept={() => {
      trackAddToCart(23.98);
      setShowUpsell(false);
      window.open('https://rutinasimple.com//cart/52248029200665:1', '_blank');
    }} onDecline={() => {
      trackAddToCart(9.99);
      setShowUpsell(false);
      window.open('https://rutinasimple.com//cart/52247734026521:1', '_blank');
    }} onClose={() => setShowUpsell(false)} data-id="element-271" />
    </section>;
}